import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Divider,
  Chip,
  Grid2,
  IconButton,
  Card,
  Tooltip,
} from "@mui/material";
import DOMPurify from "dompurify";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Report } from "@mui/icons-material";
import SimilarAds from "./SimilarAds";
import ListedBy from "./ListedBy";
import Breadcrumb from "../Breadcrumbs";
import ReportModal from "./ReportModal";
import AnalyticsTracker from "../GASetup/AnalyticsTracker";
import FormatTime from "../AdsPage/FormatTime";
import NewImagesSlider from "./NewImagesSlider";
import LoadingAnimation from "../Animations/LoadingAnimation";
import {
  cardOutline,
  fonts,
  headingFonts,
  noOutline,
  semiBoldFonts,
} from "../../Styles/Styles";
import AdNotFound from "./AdNotFound";

export default function AdDetails() {
  const { uniqueId } = useParams();
  const [adDetails, setAdDetails] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAdDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/${uniqueId}`
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        setAdDetails(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    // Increment views count
    const incrementViews = async () => {
      try {
        await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/increment-views/${uniqueId}`,
          { method: "PATCH" }
        );
      } catch (error) {
        console.error("Failed to increment view count:", error);
      }
    };

    fetchAdDetails();
    incrementViews(); // Call the increment views API
  }, [uniqueId]);

  const handleReportSubmit = async (reason) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/report/${uniqueId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ uniqueId, reason }),
        }
      );

      if (!response.ok) throw new Error("Failed to submit report.");

      alert("Report submitted successfully!");
    } catch (error) {
      alert("Failed to report the ad. Please try again.");
    }
  };

  if (error) return <AdNotFound />;

  const breadcrumbItems = [
    { label: "Home", path: "/" },
    { label: adDetails ? adDetails.title : "All Ads", path: "#" },
  ];

  if (loading) {
    return <LoadingAnimation />;
  }

  const handleInstagramShare = () => {
    // Open Instagram in a new tab immediately
    window.open("https://www.instagram.com/", "_blank");

    // Attempt to copy the URL to clipboard
    const urlToShare = window.location.href;

    navigator.clipboard
      .writeText(urlToShare)
      .then(() => {
        alert("Link copied! Paste it in your Instagram story or DM.");
      })
      .catch(() => {
        alert("Could not copy the link. Please copy it manually.");
      });
  };

  return (
    <Container sx={{ mt: 4, width: "95%" }} maxWidth={false}>
      <Breadcrumb items={breadcrumbItems} />
      {adDetails && (
        <Box>
          <Helmet>
            <title>{adDetails.title} - Listed.ky</title>
            <meta name="description" content={adDetails.description} />

            {/* Open Graph tags */}
            <meta property="og:type" content="website" />
            <meta
              property="og:title"
              content={`${adDetails.title} - Listed.ky`}
            />
            <meta property="og:description" content={adDetails.description} />
            <meta
              property="og:url"
              content={`https://www.listed.ky/ad/${adDetails.id}`}
            />
            <meta property="og:image" content={adDetails.images[0]} />
            <meta property="og:image:alt" content={adDetails.title} />
            <meta property="og:site_name" content="Listed.ky" />

            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta
              name="twitter:title"
              content={`${adDetails.title} - Listed.ky`}
            />
            <meta name="twitter:description" content={adDetails.description} />
            <meta name="twitter:image" content={adDetails.images[0]} />
          </Helmet>
          <AnalyticsTracker />
          <Grid2 container spacing={4}>
            {/* Left Section: Ad Slider and Details */}
            <Grid2 item size={{ xs: 12, md: 8 }}>
              <NewImagesSlider adDetails={adDetails} />

              <Card
                sx={{
                  p: 2,
                  mt: 2,
                  ...cardOutline,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 1 }}
                >
                  <Chip
                    label={`$ ${adDetails.price.toLocaleString()}`}
                    style={{
                      fontFamily: "ClashGrotesk-Medium",
                      letterSpacing: 1,
                      backgroundColor: "var(--primary-color)",
                      color: "white",
                      fontSize: "1.4rem",
                      borderRadius: "0",
                    }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => setIsReportModalOpen(true)}
                    aria-label="Report Ad"
                    sx={{ ...noOutline }}
                  >
                    <Report />
                  </IconButton>

                  {/* Report Modal */}
                  <ReportModal
                    open={isReportModalOpen}
                    onClose={() => setIsReportModalOpen(false)}
                    onSubmit={handleReportSubmit}
                  />
                </Box>
                {/* Title and View Count */}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 1 }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      mr: 1,
                      ...semiBoldFonts,
                    }}
                  >
                    {adDetails.title}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  mb={1}
                >
                  {/* Time */}
                  <Box display="flex" alignItems="center">
                    <AccessTimeOutlinedIcon
                      sx={{
                        fontSize: "18px",
                        mr: 0.5,
                        color: "var(--primary-color)",
                      }}
                    />
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ ...fonts }}
                    >
                      <FormatTime createdAt={adDetails.createdAt} />
                    </Typography>
                  </Box>
                  {/* Divider */}
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ ...fonts }}
                  >
                    |
                  </Typography>
                  {/* Views */}
                  <Box display="flex" alignItems="center">
                    <VisibilityOutlinedIcon
                      sx={{
                        fontSize: "18px",
                        mr: 0.5,
                        color: "var(--primary-color)",
                      }}
                    />
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ ...fonts }}
                    >
                      {adDetails.views} {adDetails.views > 1 ? "views" : "view"}
                    </Typography>
                  </Box>
                </Box>

                {/* Location */}
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    ...fonts,
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <PlaceOutlinedIcon
                    sx={{
                      fontSize: "18px",
                      mr: 0.5,
                      color: "var(--primary-color)",
                    }}
                  />
                  {adDetails.location}
                </Typography>
                {/* Category */}
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    ...fonts,
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CategoryOutlinedIcon
                    sx={{
                      fontSize: "18px",
                      mr: 0.5,
                      color: "var(--primary-color)",
                    }}
                  />
                  {adDetails.category}
                </Typography>
              </Card>
              <Card
                sx={{
                  p: 2,
                  mt: 2,
                  mb: 5,
                  ...cardOutline,
                }}
              >
                {adDetails.category !== "Vehicles" && (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        ...headingFonts,
                        mb: 2,
                      }}
                    >
                      Description
                    </Typography>
                    <Box
                      sx={{
                        ...fonts,
                        mb: 2,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          adDetails.description
                            .replace(/<h1>/g, "<h2>")
                            .replace(/<\/h1>/g, "</h2>")
                            .replace(/<h2>/g, "<h3>")
                            .replace(/<\/h2>/g, "</h3>")
                            .replace(/<h3>/g, "<h5>")
                            .replace(/<\/h3>/g, "</h5>")
                            .replace(/<h4>/g, "<h6>")
                            .replace(/<\/h4>/g, "</h6>")
                        ),
                      }}
                    />
                  </>
                )}
                {adDetails.category === "Vehicles" && (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        ...headingFonts,
                        mb: 2,
                      }}
                    >
                      Interior Features
                    </Typography>
                    <Box
                      sx={{
                        ...fonts,
                        mb: 2,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          adDetails.interiorFeatures
                            .replace(/<h1>/g, "<h2>")
                            .replace(/<\/h1>/g, "</h2>")
                            .replace(/<h2>/g, "<h3>")
                            .replace(/<\/h2>/g, "</h3>")
                            .replace(/<h3>/g, "<h5>")
                            .replace(/<\/h3>/g, "</h5>")
                            .replace(/<h4>/g, "<h6>")
                            .replace(/<\/h4>/g, "</h6>")
                        ),
                      }}
                    />
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        padding: "16px",
                        marginTop: "16px",
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                          },
                          gap: 2,
                        }}
                      >
                        {adDetails.make && adDetails.make !== "undefined" && (
                          <Typography sx={{ ...fonts }}>
                            <strong>Make: </strong>
                            {adDetails.make}
                          </Typography>
                        )}
                        {adDetails.model && adDetails.model !== "undefined" && (
                          <Typography sx={{ ...fonts }}>
                            <strong>Model: </strong>
                            {adDetails.model}
                          </Typography>
                        )}
                        {adDetails.year && adDetails.year !== "undefined" && (
                          <Typography sx={{ ...fonts }}>
                            <strong>Year: </strong>
                            {adDetails.year}
                          </Typography>
                        )}
                        {adDetails.engineSize &&
                          adDetails.engineSize !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Engine Size: </strong>
                              {adDetails.engineSize}
                            </Typography>
                          )}
                        {adDetails.bodyStyle &&
                          adDetails.bodyStyle !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Body Style: </strong>
                              {adDetails.bodyStyle}
                            </Typography>
                          )}
                        {adDetails.trimLevel &&
                          adDetails.trimLevel !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Trim Level: </strong>
                              {adDetails.trimLevel}
                            </Typography>
                          )}
                        {adDetails.transmission &&
                          adDetails.transmission !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Transmission: </strong>
                              {adDetails.transmission}
                            </Typography>
                          )}
                        {adDetails.drivetrain &&
                          adDetails.drivetrain !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Drivetrain: </strong>
                              {adDetails.drivetrain}
                            </Typography>
                          )}
                        {adDetails.fuelType &&
                          adDetails.fuelType !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Fuel Type: </strong>
                              {adDetails.fuelType}
                            </Typography>
                          )}
                        {adDetails.mileage &&
                          adDetails.mileage !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Mileage: </strong>
                              {adDetails.mileage} {adDetails.mileageUnit}
                            </Typography>
                          )}
                        {adDetails.condition &&
                          adDetails.condition !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Condition: </strong>
                              {adDetails.condition}
                            </Typography>
                          )}
                      </Box>
                    </Box>
                  </>
                )}
                {adDetails.category === "Real Estate" && (
                  <>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        padding: "16px",
                        marginTop: "16px",
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                          },
                          gap: 2,
                        }}
                      >
                        {adDetails.propertyType &&
                          adDetails.propertyType !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Property Type: </strong>
                              {adDetails.propertyType}
                            </Typography>
                          )}
                        {adDetails.listingType &&
                          adDetails.listingType !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Listing Type: </strong>
                              {adDetails.listingType}
                            </Typography>
                          )}
                        {adDetails.availableFrom &&
                          adDetails.availableFrom !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Available From: </strong>
                              {new Date(
                                adDetails.availableFrom
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </Typography>
                          )}
                        {adDetails.bedrooms &&
                          adDetails.bedrooms !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Bedrooms: </strong>
                              {adDetails.bedrooms}
                            </Typography>
                          )}
                        {adDetails.bathrooms &&
                          adDetails.bathrooms !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Bathrooms: </strong>
                              {adDetails.bathrooms}
                            </Typography>
                          )}
                        {adDetails.squareFootage &&
                          adDetails.squareFootage !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Square Footage: </strong>
                              {adDetails.squareFootage}
                            </Typography>
                          )}
                        {adDetails.lotSize &&
                          adDetails.lotSize !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Lot Size: </strong>
                              {adDetails.lotSize}
                            </Typography>
                          )}
                        {adDetails.yearBuilt &&
                          adDetails.yearBuilt !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Year Built: </strong>
                              {adDetails.yearBuilt}
                            </Typography>
                          )}
                        {adDetails.furnishingStatus &&
                          adDetails.furnishingStatus !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Furnishing Status: </strong>
                              {adDetails.furnishingStatus}
                            </Typography>
                          )}
                        {adDetails.parking &&
                          adDetails.parking !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Parking: </strong>
                              {adDetails.parking}
                            </Typography>
                          )}
                        {adDetails.floorLevel &&
                          adDetails.floorLevel !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Floor Level: </strong>
                              {adDetails.floorLevel}
                            </Typography>
                          )}
                        {adDetails.totalFloors &&
                          adDetails.totalFloors !== "undefined" && (
                            <Typography sx={{ ...fonts }}>
                              <strong>Total Floors: </strong>
                              {adDetails.totalFloors}
                            </Typography>
                          )}
                      </Box>
                    </Box>
                  </>
                )}
              </Card>

              <SimilarAds adDetails={adDetails} />
            </Grid2>

            {/* Right Section: Listed By and Location */}
            <Grid2 item size={{ xs: 12, md: 4 }}>
              <ListedBy
                userId={adDetails.userId}
                adId={adDetails._id}
                uniqueId={adDetails.uniqueId}
                isSold={adDetails.isSold}
                receiveOffers={adDetails.receiveOffers}
                minOfferAmount={adDetails.minOfferAmount}
              />

              <Card
                sx={{
                  p: 2,
                  mt: 2,
                  ...cardOutline,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    ...headingFonts,
                    mb: 1,
                  }}
                >
                  Location
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {/* Location */}
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    ...fonts,
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <PlaceOutlinedIcon
                    sx={{
                      fontSize: "18px",
                      mr: 0.5,
                      color: "var(--primary-color)",
                    }}
                  />
                  {adDetails.location}
                </Typography>
              </Card>
              <Card
                sx={{
                  p: 2,
                  mt: 2,
                  mb: 3,
                  ...cardOutline,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    ...headingFonts,
                    mb: 1,
                  }}
                >
                  Share Ad
                </Typography>
                <Divider sx={{ mb: 1 }} />

                {/* Social Media Share Buttons */}
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  {/* Facebook */}
                  <Tooltip
                    arrow
                    title={
                      <Typography variant="body2" sx={{ ...fonts }}>
                        Share on Facebook
                      </Typography>
                    }
                  >
                    <IconButton
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                        window.location.href
                      )}`}
                      target="_blank"
                      sx={{
                        color: "inherit",
                        ...cardOutline,
                        "&:hover": { color: "var(--secondary-color)" },
                      }}
                    >
                      <FacebookOutlinedIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip
                    arrow
                    title={
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "ClashGrotesk-Regular",
                          letterSpacing: 1,
                        }}
                      >
                        Share on Instagram
                      </Typography>
                    }
                  >
                    <IconButton
                      onClick={handleInstagramShare}
                      sx={{
                        color: "inherit",
                        border: "1px solid #ccc", // example cardOutline styling
                        "&:hover": { color: "var(--secondary-color)" },
                      }}
                    >
                      <InstagramIcon />
                    </IconButton>
                  </Tooltip>
                  {/* WhatsApp */}
                  <Tooltip
                    arrow
                    title={
                      <Typography variant="body2" sx={{ ...fonts }}>
                        Share on WhatsApp
                      </Typography>
                    }
                  >
                    <IconButton
                      href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                        window.location.href
                      )}`}
                      target="_blank"
                      sx={{
                        color: "inherit",
                        ...cardOutline,
                        "&:hover": { color: "var(--secondary-color)" },
                      }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Grid2>
          </Grid2>
        </Box>
      )}
    </Container>
  );
}
